import {
  DEFAULT_RESPONSIVE_FLOW,
  type BlockStyle,
  type ResponsiveFlowPatch,
} from "@/lib/cms-puck/block-style";
import {
  clampColumnCount,
  MAX_CONTENT_COLUMNS,
} from "@/lib/cms-puck/blocks/column-count";
import { normalizeGalleryColumns } from "@/lib/cms-puck/blocks/gallery";
import {
  normalizeColumnsCount,
  resolveGapPx,
  type LayoutAlign,
  type LayoutDirection,
  type LayoutGap,
  type LayoutJustify,
} from "@/lib/cms-puck/layout";

/** Texte + image : une colonne image, une colonne texte. */
const TEXT_IMAGE_COLUMNS = 2;

/** Grilles d'œuvres du catalogue (listing, recherche) : écart fixe `gap-8`. */
export const CATALOG_GRID_BLOCKS = new Set([
  "CatalogueListing",
  "CatalogueSearchResults",
]);
const CATALOG_GRID_GAP_PX = 32;

/**
 * Disposition Bureau effective d'un conteneur, lue dans ses props (onglet
 * Contenu) et exprimée dans les champs de `style.responsive.*.flow`. Même
 * correspondance que la JSDoc de `ResponsiveFlowPatch` : `gapRow` = écart
 * horizontal (`columnGap`), `gapCol` = écart vertical (`rowGap`).
 */
export function desktopFlowFromProps(
  blockType: string,
  props: Record<string, unknown>,
): ResponsiveFlowPatch {
  const gap = (props.gap as LayoutGap | undefined) ?? "none";
  if (blockType === "Container") {
    return {
      ...DEFAULT_RESPONSIVE_FLOW,
      direction: (props.direction as LayoutDirection | undefined) ?? "column",
      alignItems: (props.align as LayoutAlign | undefined) ?? "stretch",
      justify: (props.justify as LayoutJustify | undefined) ?? "start",
      gap: resolveGapPx({
        gap,
        gapCustomPx: props.gapCustomPx as number | null | undefined,
      }).columnGap,
    };
  }
  if (blockType === "TextImage") {
    // Grille image + texte : 2 colonnes à partir de la tablette, 1 en mobile.
    const gaps = resolveGapPx({
      gap,
      gapCustomPx: props.gapCustomPx as number | null | undefined,
    });
    return {
      ...DEFAULT_RESPONSIVE_FLOW,
      columns: TEXT_IMAGE_COLUMNS,
      alignItems: (props.alignItems as LayoutAlign | undefined) ?? "center",
      columnGap: gaps.columnGap,
      rowGap: gaps.rowGap,
    };
  }
  if (CATALOG_GRID_BLOCKS.has(blockType)) {
    return {
      ...DEFAULT_RESPONSIVE_FLOW,
      // 4 par défaut, comme le bloc (defaultProps) et son rendu.
      columns: normalizeGalleryColumns(props.columns ?? 4),
      alignItems: "stretch",
      columnGap: CATALOG_GRID_GAP_PX,
      rowGap: CATALOG_GRID_GAP_PX,
    };
  }
  const gaps = resolveGapPx({
    gap,
    gapCustomPx: props.gapCustomPx as number | null | undefined,
    gapRow: props.gapRow as LayoutGap | null | undefined,
    gapCol: props.gapCol as LayoutGap | null | undefined,
    gapRowCustomPx: props.gapRowCustomPx as number | null | undefined,
    gapColCustomPx: props.gapColCustomPx as number | null | undefined,
  });
  return {
    ...DEFAULT_RESPONSIVE_FLOW,
    columns:
      blockType === "Columns"
        ? normalizeColumnsCount(props.columns)
        : clampColumnCount(props.columns),
    alignItems: (props.verticalAlign as LayoutAlign | undefined) ?? "stretch",
    columnGap: gaps.columnGap,
    rowGap: gaps.rowGap,
  };
}

/**
 * Valeurs « héritées » affichées pour un appareil : tablette ← Bureau, mobile ←
 * tablette ← Bureau. Colonnes empilées sur mobile : 1 colonne en mobile, comme
 * au rendu (classe d'empilement ou `resolveColumnsBlockStyle`).
 */
export function inheritedFlow(
  device: "tablet" | "mobile",
  blockType: string,
  props: Record<string, unknown>,
  style: BlockStyle,
): ResponsiveFlowPatch {
  const desktop = desktopFlowFromProps(blockType, props);
  if (device === "tablet") return desktop;
  const tablet = style.responsive.tablet.flow;
  const merged: ResponsiveFlowPatch = {
    direction: tablet.direction ?? desktop.direction,
    alignItems: tablet.alignItems ?? desktop.alignItems,
    justify: tablet.justify ?? desktop.justify,
    gap: tablet.gap ?? desktop.gap,
    columnGap: tablet.columnGap ?? desktop.columnGap,
    rowGap: tablet.rowGap ?? desktop.rowGap,
    columns:
      validFlowColumns(tablet.columns, blockType, props) ?? desktop.columns,
  };
  if (blockType === "Columns" && props.stackOnMobile) merged.columns = 1;
  // Texte + image et grilles du catalogue s'empilent en mobile (une colonne
  // sous 640px) sauf surcharge tablette.
  if (
    (blockType === "TextImage" || CATALOG_GRID_BLOCKS.has(blockType)) &&
    tablet.columns == null
  ) {
    merged.columns = 1;
  }
  return merged;
}

/**
 * Plafond des colonnes surchargées : un bloc Colonnes n'a que N emplacements
 * (N = colonnes Bureau), une Grille répartit librement ses enfants.
 */
export function maxFlowColumns(
  blockType: string,
  props: Record<string, unknown>,
): number {
  if (blockType === "TextImage") return TEXT_IMAGE_COLUMNS;
  return blockType === "Columns"
    ? normalizeColumnsCount(props.columns)
    : MAX_CONTENT_COLUMNS;
}

/**
 * Surcharge de colonnes encore valable : au-delà du plafond, elle est restée
 * d'un réglage Bureau plus large et le rendu l'ignore (resolveColumnsBlockStyle).
 */
export function validFlowColumns(
  columns: number | null,
  blockType: string,
  props: Record<string, unknown>,
): number | null {
  return columns != null && columns <= maxFlowColumns(blockType, props)
    ? columns
    : null;
}
